import { FC, ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Divider } from 'antd';
import {
    TbAdjustmentsHorizontal,
    TbBuildingFactory2,
    TbCashBanknote,
    TbChartBar,
    TbChartDots2,
    TbChartLine,
    TbMapPin,
    TbScale,
} from 'react-icons/tb';

import { exchangeVariantQueryOptions } from '@/queries';
import { FinancialValueDisplayProvider } from '@/shared/components/financial-values';
import EmptySection from '@/shared/components/layouts/views/EmptySection';
import { ButtonSkeleton } from '@/shared/components/skeletons';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';

import {
    FINANCIAL_OVERVIEW_METRICS,
    getDefaultOverviewConfiguration,
    resolveLatestFinancialOverviewMarketPrice,
} from '../../configuration';
import { useFinancialOverviewViewPreference } from '../../hooks/useFinancialOverviewViewPreference';
import {
    FinancialOverviewChartMetric,
    FinancialOverviewChartRange,
    FinancialOverviewComparisonCompany,
    FinancialOverviewConfiguration,
    FinancialOverviewKeyEventKey,
    FinancialOverviewRightSectionProps,
    FinancialOverviewTickerDataPoint,
} from '../../types';
import { FinancialOverviewCapitalStructure } from '../FinancialOverviewCapitalStructure';
import { FinancialOverviewChartDetail } from '../FinancialOverviewChartDetail';
import { FinancialOverviewChartHeader } from '../FinancialOverviewChartHeader';
import { FinancialOverviewCorporateDebt } from '../FinancialOverviewCorporateDebt';
import { FinancialOverviewCustomizeModal } from '../FinancialOverviewCustomizeModal';
import { FinancialOverviewIndustryKpis, FinancialOverviewSectorKpis } from '../FinancialOverviewIndustryKpis';
import { FinancialOverviewKeyStatistics } from '../FinancialOverviewKeyStatistics';
import { FinancialOverviewMapPanel } from '../FinancialOverviewMapPanel';
import { FinancialOverviewMarketStatistic } from '../FinancialOverviewMarketStatistic';
import { FinancialOverviewPanel } from '../FinancialOverviewPanel';
import { FinancialOverviewSecurityPriceDropdown } from '../FinancialOverviewSecurityPriceDropdown';
import { resolveFinancialOverviewSecurityPrices } from '../FinancialOverviewSecurityPriceDropdown/prices';
import {
    FinancialOverviewChartSkeleton,
    FinancialOverviewMapSkeleton,
    FinancialOverviewStatisticsSkeleton,
} from '../FinancialOverviewSkeletons';

const CHART_RANGES: FinancialOverviewChartRange[] = [
    'realTime',
    '1D',
    '5D',
    '1W',
    '1M',
    'QTD',
    '1Q',
    '6M',
    'YTD',
    '1Y',
    '2Y',
    '5Y',
    '10Y',
    'Max',
    'ALL',
];

const isChartRange = (value: unknown): value is FinancialOverviewChartRange =>
    typeof value === 'string' && CHART_RANGES.includes(value as FinancialOverviewChartRange);

const isStringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === 'string');

const isOverviewConfiguration = (value: unknown): value is FinancialOverviewConfiguration => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Partial<FinancialOverviewConfiguration>;
    const visibility = candidate.sectionVisibility;

    return (
        isStringArray(candidate.detailMetricKeys) &&
        isStringArray(candidate.summaryMetricKeys) &&
        Boolean(visibility) &&
        ['chart', 'industryKpis', 'keyMetrics', 'map', 'sectorKpis'].every(
            (key) => typeof visibility?.[key as keyof typeof visibility] === 'boolean',
        )
    );
};

export const FinancialOverviewRightSection: FC<FinancialOverviewRightSectionProps> = ({
    allData,
    chartPreferenceKey = 'default',
    comparisonCompany,
    dividerStyle,
    entityKind = 'publicCompany',
    layout = 'legacy',
    location,
    locationDescription,
    locations,
    loading,
    ready,
    renderVerticalLayout,
    tickerSymbol,
}) => {
    const [data, setData] = useState<FinancialOverviewTickerDataPoint[]>([]);
    const [customizeStatisticsModalOpen, setCustomizeStatisticsModalOpen] = useState(false);
    const [comparisonCompanies, setComparisonCompanies] = useState<FinancialOverviewComparisonCompany[]>([]);
    const exchangeVariantsQuery = useQuery({
        ...exchangeVariantQueryOptions.bySymbol(tickerSymbol ?? ''),
        enabled: Boolean(tickerSymbol) && (entityKind === 'publicCompany' || entityKind === 'publicFund'),
    });
    const { preferences, setPreference } = useUserPreferences();
    const defaultConfiguration = useMemo(() => getDefaultOverviewConfiguration(entityKind), [entityKind]);
    const chartPreferences = (
        preferences?.financialOverview as
            | {
                  charts?: Record<string, { keyEvents?: FinancialOverviewKeyEventKey[]; metric?: string }>;
              }
            | undefined
    )?.charts?.[chartPreferenceKey];
    const keyEvents = chartPreferences?.keyEvents ?? [];
    const metric: FinancialOverviewChartMetric =
        chartPreferences?.metric === 'price' ||
        chartPreferences?.metric === 'volume' ||
        chartPreferences?.metric === 'changePercent'
            ? chartPreferences.metric
            : 'price';
    const [chartRange, setChartRange] = useFinancialOverviewViewPreference(
        chartPreferenceKey,
        'chartRange',
        '1Y',
        isChartRange,
    );
    const [configuration, setConfiguration] = useFinancialOverviewViewPreference(
        chartPreferenceKey,
        'configuration',
        defaultConfiguration,
        isOverviewConfiguration,
    );
    const financialValueDisplay = preferences?.financialOverview?.views?.[chartPreferenceKey]?.financialValueDisplay;
    const setFinancialValueDisplay = (value: typeof financialValueDisplay) => {
        setPreference(`financialOverview.views.${chartPreferenceKey}.financialValueDisplay`, value ?? null);
    };
    const withFinancialValueDisplay = (children: ReactNode) => (
        <FinancialValueDisplayProvider surfaceKey={chartPreferenceKey}>{children}</FinancialValueDisplayProvider>
    );

    const handleKeyEventsChange = (nextEvents: FinancialOverviewKeyEventKey[]) => {
        setPreference(`financialOverview.charts.${chartPreferenceKey}.keyEvents`, nextEvents);
    };

    const handleMetricChange = (nextMetric: FinancialOverviewChartMetric) => {
        setPreference(`financialOverview.charts.${chartPreferenceKey}.metric`, nextMetric);
    };

    const sectionDividerStyle = dividerStyle ?? { margin: '1.125rem 0' };
    const shouldShowChart = configuration.sectionVisibility.chart;
    const shouldShowKeyMetrics = configuration.sectionVisibility.keyMetrics;
    const industryKpis = allData.industryKpis;
    const industryKpiOptions = allData.industryKpiOptions ?? (industryKpis ? [industryKpis] : []);
    const shouldShowIndustryKpis =
        configuration.sectionVisibility.industryKpis &&
        (loading || industryKpiOptions.some((section) => section.metrics.length > 0));
    const sectorKpis = allData.sectorKpis;
    const sectorKpiOptions = allData.sectorKpiOptions ?? (sectorKpis ? [sectorKpis] : []);
    const shouldShowSectorKpis =
        configuration.sectionVisibility.sectorKpis &&
        (loading || sectorKpiOptions.some((section) => section.metrics.length > 0));
    const shouldShowMap = configuration.sectionVisibility.map;
    const shouldShowCapitalAnalysis = entityKind === 'privateCompany' || entityKind === 'publicCompany';
    const hasContentBelowChart =
        shouldShowKeyMetrics || shouldShowIndustryKpis || shouldShowSectorKpis || shouldShowMap;
    const chartMarketPrice = resolveLatestFinancialOverviewMarketPrice(data);
    const fallbackMarketPrice = comparisonCompany?.price ?? chartMarketPrice;
    const securityPrices = useMemo(
        () =>
            tickerSymbol
                ? resolveFinancialOverviewSecurityPrices(exchangeVariantsQuery.data ?? [], {
                      currency: comparisonCompany?.currency,
                      exchange: comparisonCompany?.exchange,
                      name: comparisonCompany?.companyName,
                      price: fallbackMarketPrice,
                      symbol: tickerSymbol,
                  })
                : [],
        [
            comparisonCompany?.companyName,
            comparisonCompany?.currency,
            comparisonCompany?.exchange,
            exchangeVariantsQuery.data,
            fallbackMarketPrice,
            tickerSymbol,
        ],
    );
    const currentSecurityPrice = securityPrices.find((price) => price.current);
    const marketPrice = currentSecurityPrice?.price ?? fallbackMarketPrice;
    const marketPriceAsOf = currentSecurityPrice?.timestamp
        ? currentSecurityPrice.timestamp < 1_000_000_000_000
            ? currentSecurityPrice.timestamp * 1000
            : currentSecurityPrice.timestamp
        : exchangeVariantsQuery.data?.some(
                (variant) =>
                    variant.symbol.trim().toUpperCase() === tickerSymbol?.trim().toUpperCase() &&
                    typeof variant.price === 'number' &&
                    Number.isFinite(variant.price),
            )
          ? exchangeVariantsQuery.dataUpdatedAt
          : undefined;
    const renderSecurityPriceAction = () => (
        <FinancialOverviewSecurityPriceDropdown
            companyName={comparisonCompany?.companyName}
            prices={securityPrices}
            updatedAt={exchangeVariantsQuery.dataUpdatedAt || undefined}
            website={comparisonCompany?.website}
        />
    );
    const customizeButton = (
        <Button
            icon={<TbAdjustmentsHorizontal style={{ verticalAlign: '-0.125rem' }} />}
            onClick={() => setCustomizeStatisticsModalOpen(true)}
            size='small'
        >
            Customize
        </Button>
    );
    const chartContent =
        ready && !loading && tickerSymbol ? (
            <>
                <FinancialOverviewChartHeader
                    chartRange={chartRange}
                    comparisonCompanies={comparisonCompanies}
                    comparisonCompany={comparisonCompany}
                    keyEvents={keyEvents}
                    metric={metric}
                    onComparisonCompaniesChange={setComparisonCompanies}
                    onKeyEventsChange={handleKeyEventsChange}
                    onMetricChange={handleMetricChange}
                    setChartRange={setChartRange}
                />
                <FinancialOverviewChartDetail
                    aspect={layout === 'vertical' ? 3 : undefined}
                    chartRange={chartRange}
                    chartType='area'
                    comparisonCompanies={comparisonCompanies}
                    keyEvents={keyEvents}
                    metric={metric}
                    setTickerData={setData}
                    showVolume={false}
                    tickerSymbol={tickerSymbol}
                />
            </>
        ) : loading ? (
            <FinancialOverviewChartSkeleton />
        ) : (
            <EmptySection
                description='A public-market ticker is required to display historical performance.'
                height='18rem'
                icon={<TbChartLine size='1.25rem' />}
                label='Market Performance Unavailable'
            />
        );
    const keyMetricsContent =
        ready && !loading ? (
            <FinancialOverviewKeyStatistics
                tickerSymbol={tickerSymbol}
                allData={allData}
                chartData={data}
                chartRange={chartRange}
                configuration={configuration}
                marketPrice={marketPrice}
                priceAction={renderSecurityPriceAction()}
            />
        ) : (
            <FinancialOverviewStatisticsSkeleton />
        );
    const industryKpisContent =
        ready && !loading && industryKpis ? (
            <FinancialOverviewIndustryKpis
                preferenceKey={chartPreferenceKey}
                section={industryKpis}
                sections={industryKpiOptions}
            />
        ) : loading ? (
            <FinancialOverviewPanel
                actions={<ButtonSkeleton size='small' style={{ width: '10rem' }} />}
                description='Review industry-specific operating indicators and sector benchmarks.'
                icon={<TbBuildingFactory2 style={{ verticalAlign: '-0.125rem' }} />}
                title='Industry KPIs'
            >
                <FinancialOverviewStatisticsSkeleton detailRows={0} />
            </FinancialOverviewPanel>
        ) : (
            <EmptySection
                description='No industry-specific operating indicators are currently available for this company.'
                height='14rem'
                icon={<TbBuildingFactory2 size='1.25rem' />}
                label='KPIs Unavailable'
            />
        );
    const sectorKpisContent =
        ready && !loading && sectorKpis ? (
            <FinancialOverviewSectorKpis
                preferenceKey={chartPreferenceKey}
                section={sectorKpis}
                sections={sectorKpiOptions}
            />
        ) : loading ? (
            <FinancialOverviewPanel
                actions={<ButtonSkeleton size='small' style={{ width: '10rem' }} />}
                description='Review sector-specific operating indicators and calculated efficiency metrics.'
                icon={<TbChartDots2 style={{ verticalAlign: '-0.125rem' }} />}
                title='Sector KPIs'
            >
                <FinancialOverviewStatisticsSkeleton detailRows={0} />
            </FinancialOverviewPanel>
        ) : (
            <EmptySection
                description='No sector-specific operating indicators are currently available for this company.'
                height='14rem'
                icon={<TbChartDots2 size='1.25rem' />}
                label='Sector KPIs Unavailable'
            />
        );
    const mapContent =
        ready && !loading ? (
            <FinancialOverviewMapPanel
                location={location}
                locationDescription={locationDescription}
                locations={locations}
                tickerSymbol={tickerSymbol}
            />
        ) : (
            <FinancialOverviewPanel
                actions={<ButtonSkeleton size='small' style={{ width: '9rem' }} />}
                description={locationDescription ?? 'Review headquarters and other reported company locations.'}
                icon={<TbMapPin style={{ verticalAlign: '-0.125rem' }} />}
                title='Location'
            >
                <FinancialOverviewMapSkeleton />
            </FinancialOverviewPanel>
        );
    const corporateDebtContent = loading ? (
        <FinancialOverviewPanel
            actions={<ButtonSkeleton size='small' style={{ width: '10rem' }} />}
            description='Review debt composition, reported financing instruments, and liquidity coverage.'
            icon={<TbCashBanknote style={{ verticalAlign: '-0.125rem' }} />}
            title='Corporate Debt'
        >
            <FinancialOverviewStatisticsSkeleton detailRows={2} />
        </FinancialOverviewPanel>
    ) : (
        <FinancialOverviewCorporateDebt
            preferenceKey={chartPreferenceKey}
            section={allData.corporateDebt}
            tickerSymbol={tickerSymbol}
        />
    );
    const capitalStructureContent = loading ? (
        <FinancialOverviewPanel
            actions={<ButtonSkeleton size='small' style={{ width: '10rem' }} />}
            description='Understand how equity value, debt, and liquidity reconcile to enterprise value.'
            icon={<TbScale style={{ verticalAlign: '-0.125rem' }} />}
            title='Capital Structure'
        >
            <FinancialOverviewStatisticsSkeleton detailRows={2} />
        </FinancialOverviewPanel>
    ) : (
        <FinancialOverviewCapitalStructure preferenceKey={chartPreferenceKey} section={allData.capitalStructure} />
    );

    if (layout === 'vertical') {
        const verticalSections = {
            marketPerformance: (
                <FinancialOverviewPanel
                    actions={
                        ready && !loading && tickerSymbol ? (
                            <FinancialOverviewMarketStatistic
                                chartRange={chartRange}
                                data={data}
                                marketPrice={marketPrice}
                                marketPriceAsOf={marketPriceAsOf}
                                priceAction={renderSecurityPriceAction()}
                            />
                        ) : loading ? (
                            <ButtonSkeleton size='small' style={{ width: '15rem' }} />
                        ) : undefined
                    }
                    description='Review historical market performance and compare related series.'
                    icon={<TbChartLine style={{ verticalAlign: '-0.125rem' }} />}
                    title='Market Performance'
                >
                    {chartContent}
                </FinancialOverviewPanel>
            ),
            keyStatistics: (
                <FinancialOverviewPanel
                    actions={
                        ready && !loading ? (
                            <Button
                                icon={<TbAdjustmentsHorizontal style={{ verticalAlign: '-0.125rem' }} />}
                                onClick={() => setCustomizeStatisticsModalOpen(true)}
                                size='small'
                            >
                                Customize Statistics
                            </Button>
                        ) : (
                            <ButtonSkeleton size='small' style={{ width: '10rem' }} />
                        )
                    }
                    description='A focused snapshot of valuation, capitalization, profitability, and operating metrics.'
                    icon={<TbChartBar style={{ verticalAlign: '-0.125rem' }} />}
                    title='Key Stats'
                >
                    {keyMetricsContent}
                </FinancialOverviewPanel>
            ),
            corporateDebt: shouldShowCapitalAnalysis ? corporateDebtContent : undefined,
            capitalStructure: shouldShowCapitalAnalysis ? capitalStructureContent : undefined,
            sectorKpis: sectorKpisContent,
            kpis: industryKpisContent,
            location: mapContent,
        };

        return withFinancialValueDisplay(
            <>
                {renderVerticalLayout ? (
                    renderVerticalLayout(verticalSections)
                ) : (
                    <div style={{ display: 'grid', gap: '1.5rem' }}>
                        {Object.entries(verticalSections).map(([key, section]) =>
                            section ? <div key={key}>{section}</div> : null,
                        )}
                    </div>
                )}
                {customizeStatisticsModalOpen ? (
                    <FinancialOverviewCustomizeModal
                        configuration={configuration}
                        financialValueDisplay={financialValueDisplay}
                        metricOptions={FINANCIAL_OVERVIEW_METRICS}
                        onChange={setConfiguration}
                        onClose={() => setCustomizeStatisticsModalOpen(false)}
                        onFinancialValueDisplayChange={setFinancialValueDisplay}
                        open={customizeStatisticsModalOpen}
                        showSectionOptions={false}
                        title='Customize Statistics'
                    />
                ) : null}
            </>,
        );
    }

    return withFinancialValueDisplay(
        <>
            {shouldShowChart && (
                <div>
                    {ready && !loading && tickerSymbol ? (
                        chartContent
                    ) : loading ? (
                        <FinancialOverviewChartSkeleton />
                    ) : (
                        <EmptySection
                            description='A public-market ticker is required to display historical performance.'
                            height='18rem'
                            icon={<TbChartLine size='1.25rem' />}
                            label='Market Performance Unavailable'
                        />
                    )}
                </div>
            )}
            {shouldShowChart && hasContentBelowChart && <Divider orientation='right' style={sectionDividerStyle} />}
            {shouldShowKeyMetrics && keyMetricsContent}
            {shouldShowKeyMetrics && (shouldShowIndustryKpis || shouldShowSectorKpis || shouldShowMap) && (
                <Divider orientation='right' style={sectionDividerStyle} />
            )}
            {shouldShowSectorKpis && sectorKpisContent}
            {shouldShowSectorKpis && (shouldShowIndustryKpis || shouldShowMap) && (
                <Divider orientation='right' style={sectionDividerStyle} />
            )}
            {shouldShowIndustryKpis && industryKpisContent}
            {shouldShowIndustryKpis && shouldShowMap && <Divider orientation='right' style={sectionDividerStyle} />}
            {shouldShowMap && mapContent}
            {(!shouldShowChart || !tickerSymbol) && ready && !loading && (
                <div style={{ marginTop: hasContentBelowChart ? '1rem' : 0 }}>{customizeButton}</div>
            )}
            {customizeStatisticsModalOpen && (
                <FinancialOverviewCustomizeModal
                    configuration={configuration}
                    financialValueDisplay={financialValueDisplay}
                    metricOptions={FINANCIAL_OVERVIEW_METRICS}
                    onChange={setConfiguration}
                    onClose={() => setCustomizeStatisticsModalOpen(false)}
                    onFinancialValueDisplayChange={setFinancialValueDisplay}
                    open={customizeStatisticsModalOpen}
                />
            )}
        </>,
    );
};
